import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Translate as TranslateIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useBlogPosts,
  useCreateBlogPost,
  useUpdateBlogPost,
  useDeleteBlogPost,
} from "../../hooks/useBlogPosts";
import { useSkills } from "../../hooks/useSkills";
import { useTechnologies } from "../../hooks/useTechnologies";
import { supabase } from "../../lib/supabase";
import Select from "react-select";
import {
  adminColors,
  textFieldStyles,
  dialogStyles,
  tableStyles,
  buttonStyles,
  switchStyles,
  chipStyles,
} from "./adminStyles";
import { useTranslation } from "react-i18next";

const AdminBlog = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {
    data: posts,
    isLoading,
    error,
    refetch,
  } = useBlogPosts({ isPublished: null });
  const createPost = useCreateBlogPost();
  const updatePost = useUpdateBlogPost();
  const deletePost = useDeleteBlogPost();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);

  const { data: allSkills, isLoading: loadingSkills } = useSkills({ language: i18n.language });
  const { data: allTech, isLoading: loadingTech } = useTechnologies({ language: i18n.language });

  const [formData, setFormData] = useState({
    slug: "",
    cover_image_url: "",
    published_date: "",
    is_published: false,
    selectedSkills: [],
    selectedTech: [],
    translations: {
      fr: { title: "", content: "", excerpt: "" },
      en: { title: "", content: "", excerpt: "" },
    },
  });

  const handleOpenCreate = () => {
    setSelectedPost(null);
    setFormData({
      slug: "",
      cover_image_url: "",
      published_date: "",
      is_published: false,
      selectedSkills: [],
      selectedTech: [],
      translations: {
        fr: { title: "", content: "", excerpt: "" },
        en: { title: "", content: "", excerpt: "" },
      },
    });
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleOpenEdit = async (post) => {
    setSelectedPost(post);
    
    // Fetch associated skills and tech
    const { data: postSkills } = await supabase
      .from("blog_post_skills")
      .select("skill_id")
      .eq("blog_post_id", post.id);
      
    const { data: postTech } = await supabase
      .from("blog_post_technologies")
      .select("technology_id")
      .eq("blog_post_id", post.id);

    const frTrans =
      post.translations?.find((t) => t.language_code === "fr") || {};
    const enTrans =
      post.translations?.find((t) => t.language_code === "en") || {};

    setFormData({
      slug: post.slug || "",
      cover_image_url: post.cover_image_url || "",
      published_date: post.published_date ? new Date(post.published_date).toISOString().slice(0, 16) : "",
      is_published: post.is_published || false,
      selectedSkills: postSkills?.map(s => s.skill_id) || [],
      selectedTech: postTech?.map(t => t.technology_id) || [],
      translations: {
        fr: {
          title: frTrans.title || "",
          content: frTrans.content || "",
          excerpt: frTrans.excerpt || "",
        },
        en: {
          title: enTrans.title || "",
          content: enTrans.content || "",
          excerpt: enTrans.excerpt || "",
        },
      },
    });
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleOpenDelete = (post) => {
    setSelectedPost(post);
    setDeleteDialogOpen(true);
  };

  const handleMove = async (post, direction) => {
    const currentIndex = posts.findIndex((p) => p.id === post.id);
    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= posts.length) return;

    const targetPost = posts[targetIndex];

    try {
      const tempOrder = post.display_order || 0;
      await supabase
        .from("blog_posts")
        .update({ display_order: targetPost.display_order || 0 })
        .eq("id", post.id);
      await supabase
        .from("blog_posts")
        .update({ display_order: tempOrder })
        .eq("id", targetPost.id);
      await refetch();
    } catch (err) {
      console.error("Error reordering posts:", err);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const postData = {
        slug: formData.slug,
        cover_image_url: formData.cover_image_url || null,
        is_published: formData.is_published,
        published_date: formData.published_date || (formData.is_published ? new Date().toISOString() : null),
      };

      let postId;

      if (selectedPost) {
        await updatePost.mutateAsync({ id: selectedPost.id, ...postData });
        postId = selectedPost.id;

        // Update relations
        await supabase.from("blog_post_skills").delete().eq("blog_post_id", postId);
        if (formData.selectedSkills.length > 0) {
          await supabase.from("blog_post_skills").insert(
            formData.selectedSkills.map(skillId => ({ blog_post_id: postId, skill_id: skillId }))
          );
        }

        await supabase.from("blog_post_technologies").delete().eq("blog_post_id", postId);
        if (formData.selectedTech.length > 0) {
          await supabase.from("blog_post_technologies").insert(
            formData.selectedTech.map(techId => ({ blog_post_id: postId, technology_id: techId }))
          );
        }

        for (const lang of ["fr", "en"]) {
          const transData = {
            blog_post_id: postId,
            language_code: lang,
            title: formData.translations[lang].title,
            content: formData.translations[lang].content,
            excerpt: formData.translations[lang].excerpt,
          };

          const { data: existing } = await supabase
            .from("blog_posts_translations")
            .select("id")
            .eq("blog_post_id", postId)
            .eq("language_code", lang)
            .single();

          if (existing) {
            await supabase
              .from("blog_posts_translations")
              .update(transData)
              .eq("id", existing.id);
          } else {
            await supabase
              .from("blog_posts_translations")
              .insert({ id: crypto.randomUUID(), ...transData });
          }
        }
      } else {
        const result = await createPost.mutateAsync(postData);
        postId = result.id;

        // Insert relations
        if (formData.selectedSkills.length > 0) {
          await supabase.from("blog_post_skills").insert(
            formData.selectedSkills.map(skillId => ({ blog_post_id: postId, skill_id: skillId }))
          );
        }
        if (formData.selectedTech.length > 0) {
          await supabase.from("blog_post_technologies").insert(
            formData.selectedTech.map(techId => ({ blog_post_id: postId, technology_id: techId }))
          );
        }

        for (const lang of ["fr", "en"]) {
          await supabase.from("blog_posts_translations").insert({
            id: crypto.randomUUID(),
            blog_post_id: postId,
            language_code: lang,
            title: formData.translations[lang].title,
            content: formData.translations[lang].content,
            excerpt: formData.translations[lang].excerpt,
          });
        }
      }

      await refetch();
      setDialogOpen(false);
    } catch (err) {
      console.error("Error saving post:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deletePost.mutateAsync(selectedPost.id);
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error("Error deleting post:", err);
    }
  };

  const updateTranslation = (lang, field, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: { ...prev.translations[lang], [field]: value },
      },
    }));
  };

  if (isLoading || loadingSkills || loadingTech) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: adminColors.bg.page,
        }}
      >
        <CircularProgress sx={{ color: adminColors.accent.teal }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{ minHeight: "100vh", bgcolor: adminColors.bg.page, pt: 12, pb: 8 }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => navigate("/admin")}
              sx={{ color: adminColors.text.primary }}
            >
              <BackIcon />
            </IconButton>
            <Typography
              variant="h4"
              sx={{ color: adminColors.text.primary, fontWeight: 700 }}
            >
              {t("admin.sections.blog")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={buttonStyles.primary}
          >
            {t("admin.actions.create")}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error.message}
          </Alert>
        )}

        <TableContainer component={Paper} sx={tableStyles.container}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.title")} (FR)
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.slug")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.order")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.status")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell} align="right">
                  {t("admin.labels.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts?.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{
                      ...tableStyles.bodyCell,
                      textAlign: "center",
                      py: 4,
                      color: adminColors.text.secondary,
                    }}
                  >
                    {t("admin.messages.noData")}
                  </TableCell>
                </TableRow>
              )}
              {posts?.map((post) => (
                <TableRow key={post.id} sx={tableStyles.row}>
                  <TableCell sx={tableStyles.bodyCell}>
                    {post.title || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableStyles.bodyCell,
                      color: adminColors.text.secondary,
                    }}
                  >
                    {post.slug}
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell}>
                    <Stack direction="row">
                      <IconButton
                        size="small"
                        onClick={() => handleMove(post, "up")}
                        disabled={posts.indexOf(post) === 0}
                        sx={{ color: adminColors.accent.teal }}
                      >
                        <UpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMove(post, "down")}
                        disabled={posts.indexOf(post) === posts.length - 1}
                        sx={{ color: adminColors.accent.teal }}
                      >
                        <DownIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell}>
                    <Chip
                      label={
                        post.is_published
                          ? t("admin.labels.published")
                          : t("admin.labels.draft")
                      }
                      size="small"
                      sx={
                        post.is_published
                          ? chipStyles.published
                          : chipStyles.draft
                      }
                    />
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell} align="right">
                    <IconButton
                      onClick={() => handleOpenEdit(post)}
                      sx={buttonStyles.iconEdit}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenDelete(post)}
                      sx={buttonStyles.iconDelete}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: dialogStyles }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: adminColors.text.primary,
              borderBottom: `1px solid ${adminColors.border.default}`,
              pb: 2,
            }}
          >
            <TranslateIcon sx={{ color: adminColors.accent.teal }} />
            {selectedPost
              ? `${t("admin.actions.edit")} ${t("admin.sections.blog")}`
              : `${t("admin.actions.create")} ${t("admin.sections.blog")}`}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label={`${t("admin.labels.slug")} (URL)`}
                fullWidth
                value={formData.slug}
                onChange={(e) =>
                  setFormData({ ...formData, slug: e.target.value })
                }
                helperText={t("admin.messages.slugHelper")}
                sx={textFieldStyles}
              />

              <Box>
                <Typography variant="subtitle2" sx={{ color: adminColors.accent.teal, mb: 1 }}>
                  {t("admin.labels.skills")}
                </Typography>
                <Select
                  isMulti
                  placeholder={t("admin.labels.select")}
                  options={allSkills?.map(s => ({ value: s.id, label: s.name }))}
                  value={allSkills?.filter(s => formData.selectedSkills.includes(s.id)).map(s => ({ value: s.id, label: s.name }))}
                  onChange={(selected) => setFormData({ ...formData, selectedSkills: selected ? selected.map(s => s.value) : [] })}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: adminColors.bg.card,
                      borderColor: adminColors.border.default,
                      color: 'white'
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: adminColors.bg.card,
                      zIndex: 9999
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused ? adminColors.bg.hover : adminColors.bg.card,
                      color: 'white'
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: 'rgba(15, 163, 163, 0.2)',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: adminColors.accent.teal,
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: adminColors.accent.teal,
                      ':hover': {
                        backgroundColor: adminColors.accent.teal,
                        color: 'white',
                      },
                    }),
                  }}
                  menuPortalTarget={document.body}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ color: adminColors.accent.gold, mb: 1 }}>
                  {t("admin.labels.technologies")}
                </Typography>
                <Select
                  isMulti
                  placeholder={t("admin.labels.select")}
                  options={allTech?.map(t => ({ value: t.id, label: t.name }))}
                  value={allTech?.filter(t => formData.selectedTech.includes(t.id)).map(t => ({ value: t.id, label: t.name }))}
                  onChange={(selected) => setFormData({ ...formData, selectedTech: selected ? selected.map(t => t.value) : [] })}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: adminColors.bg.card,
                      borderColor: adminColors.border.default,
                      color: 'white'
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: adminColors.bg.card,
                      zIndex: 9999
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused ? adminColors.bg.hover : adminColors.bg.card,
                      color: 'white'
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: 'rgba(212, 175, 55, 0.2)',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: adminColors.accent.gold,
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: adminColors.accent.gold,
                      ':hover': {
                        backgroundColor: adminColors.accent.gold,
                        color: 'white',
                      },
                    }),
                  }}
                  menuPortalTarget={document.body}
                />
              </Box>

              <TextField
                label={t("admin.labels.publishedDate")}
                type="datetime-local"
                fullWidth
                value={formData.published_date}
                onChange={(e) =>
                  setFormData({ ...formData, published_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyles}
              />

              <Divider sx={{ borderColor: adminColors.border.default }} />

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: adminColors.accent.gold,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: 600,
                  }}
                >
                  <TranslateIcon fontSize="small" />{" "}
                  {t("admin.translations.title")}
                </Typography>
                <Tabs
                  value={tabValue}
                  onChange={(e, v) => setTabValue(v)}
                  sx={{
                    mb: 2,
                    "& .MuiTabs-indicator": {
                      bgcolor: adminColors.accent.teal,
                    },
                  }}
                >
                  <Tab
                    label={t("admin.translations.tabs.fr")}
                    sx={{
                      color:
                        tabValue === 0
                          ? adminColors.accent.teal
                          : adminColors.text.secondary,
                      fontWeight: tabValue === 0 ? 600 : 400,
                    }}
                  />
                  <Tab
                    label={t("admin.translations.tabs.en")}
                    sx={{
                      color:
                        tabValue === 1
                          ? adminColors.accent.teal
                          : adminColors.text.secondary,
                      fontWeight: tabValue === 1 ? 600 : 400,
                    }}
                  />
                </Tabs>

                {tabValue === 0 && (
                  <Stack spacing={2}>
                    <TextField
                      label={`${t("admin.labels.title")} (FR)`}
                      fullWidth
                      multiline
                      value={formData.translations.fr.title}
                      onChange={(e) =>
                        updateTranslation("fr", "title", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                    <TextField
                      label={`${t("admin.labels.excerpt")} (FR)`}
                      fullWidth
                      multiline
                      rows={2}
                      value={formData.translations.fr.excerpt}
                      onChange={(e) =>
                        updateTranslation("fr", "excerpt", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                    <TextField
                      label={`${t("admin.labels.content")} (FR)`}
                      fullWidth
                      multiline
                      rows={8}
                      value={formData.translations.fr.content}
                      onChange={(e) =>
                        updateTranslation("fr", "content", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                  </Stack>
                )}

                {tabValue === 1 && (
                  <Stack spacing={2}>
                    <TextField
                      label={`${t("admin.labels.title")} (EN)`}
                      fullWidth
                      multiline
                      value={formData.translations.en.title}
                      onChange={(e) =>
                        updateTranslation("en", "title", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                    <TextField
                      label={`${t("admin.labels.excerpt")} (EN)`}
                      fullWidth
                      multiline
                      rows={2}
                      value={formData.translations.en.excerpt}
                      onChange={(e) =>
                        updateTranslation("en", "excerpt", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                    <TextField
                      label={`${t("admin.labels.content")} (EN)`}
                      fullWidth
                      multiline
                      rows={8}
                      value={formData.translations.en.content}
                      onChange={(e) =>
                        updateTranslation("en", "content", e.target.value)
                      }
                      sx={textFieldStyles}
                    />
                  </Stack>
                )}
              </Box>

              <Divider sx={{ borderColor: adminColors.border.default }} />

              <TextField
                label={t("admin.labels.coverImageUrl")}
                fullWidth
                value={formData.cover_image_url}
                onChange={(e) =>
                  setFormData({ ...formData, cover_image_url: e.target.value })
                }
                sx={textFieldStyles}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_published}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        is_published: e.target.checked,
                      })
                    }
                    sx={switchStyles}
                  />
                }
                label={t("admin.labels.published")}
                sx={{ color: adminColors.text.primary }}
              />

              <Divider sx={{ borderColor: adminColors.border.default }} />
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{ p: 3, borderTop: `1px solid ${adminColors.border.default}` }}
          >
            <Button
              onClick={() => setDialogOpen(false)}
              sx={buttonStyles.secondary}
            >
              {t("admin.actions.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={saving}
              sx={buttonStyles.primary}
            >
              {saving ? (
                <CircularProgress
                  size={24}
                  sx={{ color: adminColors.bg.page }}
                />
              ) : (
                t("admin.actions.save")
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{ sx: dialogStyles }}
        >
          <DialogTitle sx={{ color: adminColors.text.primary }}>
            {t("admin.messages.confirmDeleteTitle")}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: adminColors.text.secondary }}>
              {t("admin.messages.confirmDelete")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={buttonStyles.secondary}
            >
              {t("admin.actions.cancel")}
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              disabled={deletePost.isPending}
              sx={{
                bgcolor: adminColors.status.error,
                "&:hover": { bgcolor: "#DC2626" },
              }}
            >
              {deletePost.isPending ? (
                <CircularProgress size={24} />
              ) : (
                t("admin.actions.delete")
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminBlog;
